import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, catchError, map, switchMap } from 'rxjs';
import { FileManagerService } from './file-manager.service';
import type { DirEntry } from '../models/api';
import type { FileItem, LinkPatch } from '../models';

@Injectable()
export class FileBrowserStore {
  private readonly api = inject(FileManagerService);

  readonly files = signal<FileItem[]>([]);
  readonly loading = signal(false);
  readonly storages = signal<string[]>([]);
  readonly readOnly = signal(false);
  readonly breadcrumbs = signal<string[]>([]);

  readonly folders = computed(() => this.files().filter((f) => f.type === 'folder'));
  readonly filesList = computed(() => this.files().filter((f) => f.type === 'file'));

  private readonly loadTrigger$ = new Subject<{ path: string; onLoaded?: () => void }>();
  private readonly searchTrigger$ = new Subject<{ path: string; query: string; deep: boolean }>();

  constructor() {
    this.loadTrigger$
      .pipe(
        switchMap(({ path, onLoaded }) => {
          this.loading.set(true);
          return this.api.listFiles(path).pipe(
            map((data) => ({ data, onLoaded })),
            catchError(() => {
              this.loading.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ data, onLoaded }) => {
        this.storages.set(data.storages);
        this.readOnly.set(data.read_only);
        this.updateBreadcrumbs(data.dirname);
        this.setFilesFromEntries(data.files);
        this.loading.set(false);
        onLoaded?.();
      });

    this.searchTrigger$
      .pipe(
        switchMap(({ path, query, deep }) => {
          this.loading.set(true);
          return this.api.searchFiles(path, query, deep).pipe(
            catchError(() => {
              this.loading.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        this.setFilesFromEntries(result.files);
        this.loading.set(false);
      });
  }

  load(path: string, onLoaded?: () => void): void {
    this.loadTrigger$.next({ path, onLoaded });
  }

  search(path: string, query: string, deep = false): void {
    this.searchTrigger$.next({ path, query, deep });
  }

  applyLinkPatch(patch: LinkPatch): void {
    const ids = new Set(patch.fileIds);
    if (ids.size === 0) return;

    this.files.update((items) => {
      let changed = false;
      const next = items.map((item) => {
        if (item.id === undefined || !ids.has(item.id)) return item;
        changed = true;

        const existing = item.docmatrixLinks ?? [];
        const docmatrixLinks =
          patch.kind === 'link'
            ? [...existing, patch.link]
            : existing.filter(
                (link) =>
                  link.sourcemodel_id !== patch.sourcemodelId || link.item_uuid !== patch.itemUuid,
              );

        return {
          ...item,
          links: Math.max(0, (item.links ?? 0) + (patch.kind === 'link' ? 1 : -1)),
          docmatrixLinks,
        };
      });
      return changed ? next : items;
    });
  }

  setFilesFromEntries(entries: DirEntry[]): void {
    const items = this.foldersFirst(entries.map((e) => this.mapDirEntryToFileItem(e)));
    if (this.breadcrumbs().length > 1) {
      items.unshift(this.buildParentEntry());
    }
    this.files.set(items);
  }

  private buildParentEntry(): FileItem {
    return {
      name: '..',
      type: 'folder',
      extension: '',
      date: '',
      size: '',
      isScanning: false,
    };
  }

  setLoading(value: boolean): void {
    this.loading.set(value);
  }

  private updateBreadcrumbs(dirname: string): void {
    const [storage = 'local', rest = ''] = dirname.split('://');
    const pathParts = rest.split('/').filter(Boolean);
    this.breadcrumbs.set([storage, ...pathParts]);
  }

  private foldersFirst(files: FileItem[]): FileItem[] {
    return [...files].sort((a, b) => (a.type !== b.type ? (a.type === 'folder' ? -1 : 1) : 0));
  }

  private mapDirEntryToFileItem(entry: DirEntry): FileItem {
    return {
      id: entry.id,
      name: entry.basename,
      extension: entry.type === 'dir' ? 'mappa' : (entry.extension || '').toUpperCase(),
      date: entry.last_modified
        ? new Date(entry.last_modified * 1000).toLocaleDateString('hu-HU')
        : '-',
      size: entry.type === 'dir' ? '-' : this.formatFileSize(entry.file_size),
      links: entry.plugins?.docmatrix_link_count,
      docmatrixLinks: entry.plugins?.docmatrix_links,
      type: entry.type === 'dir' ? 'folder' : 'file',
      apiPath: entry.path,
      thumbnailUrl: undefined,
      isScanning: entry.plugins?.antivirus?.is_scanned === false,
      fileCount: entry.files_count,
      directoriesCount: entry.directories_count,
    };
  }

  private formatFileSize(bytes?: number | null): string {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Kb`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mb`;
  }
}
